import React from 'react';
import { motion } from 'framer-motion';

const PartnersSection: React.FC = () => {
  const partners = [
    {
      name: "Epsilla",
      description: "AI-powered vector database platform",
      logo: (
        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 rounded-2xl flex items-center justify-center relative overflow-hidden">
          {/* Epsilla logo - stylized "e" */}
          <div className="relative">
            <div className="w-8 h-8 border-4 border-white rounded-full relative">
              <div className="absolute top-1/2 left-0 w-4 h-0.5 bg-white transform -translate-y-1/2"></div>
            </div>
            {/* Swoosh element */}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white/20 rounded-full transform rotate-45"></div>
          </div>
        </div>
      ),
      details: [
        "Open-source vector database",
        "10x faster than HNSW algorithms",
        "AI agent platform for enterprises",
        "Vertical LLM agents with private data"
      ],
      website: "epsilla.com"
    },
    {
      name: "Jobright",
      description: "AI-powered job search career platform",
      logo: (
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center relative overflow-hidden">
          {/* Jobright logo - bird/checkmark symbol */}
          <div className="relative">
            {/* Bird body */}
            <div className="w-10 h-6 bg-white rounded-full relative">
              {/* Bird head */}
              <div className="absolute -top-1 right-1 w-4 h-4 bg-white rounded-full"></div>
              {/* Beak */}
              <div className="absolute top-0 -right-1 w-3 h-1 bg-white rounded transform rotate-12"></div>
              {/* Wing detail */}
              <div className="absolute top-1 left-1 w-6 h-2 bg-green-400 rounded transform -rotate-12"></div>
            </div>
          </div>
        </div>
      ),
      details: [
        "AI job search copilot",
        "400,000+ new jobs added daily",
        "80% time savings in job search",
        "Automated resume optimization"
      ],
      website: "jobright.ai"
    }
  ];

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">
            Our <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">partners</span>
          </h2>
        </motion.div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {partners.map((partner, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 relative group hover:border-gray-700 transition-all duration-300"
            >
              {/* Logo */}
              <div className="flex justify-center mb-6">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {partner.logo}
                </motion.div>
              </div>

              {/* Partner Info */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {partner.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  {partner.description}
                </p>
              </div>

              {/* Partner Details */}
              <div className="space-y-2 mb-6">
                {partner.details.map((detail, detailIndex) => (
                  <div key={detailIndex} className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-300 text-sm">{detail}</span>
                  </div>
                ))}
              </div>

              {/* Website Link */}
              <div className="text-center">
                <motion.a
                  href={`https://${partner.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                  whileHover={{ scale: 1.05 }}
                >
                  <span>{partner.website}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </motion.a>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-400/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/3 w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-blue-300/50 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default PartnersSection;